import React from "react";

import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, Icon, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";

import CustomSnackBar from "../../Components/CustomSnackBar/CustomSnackBar";
import CustomTextInput from "../../Components/CustomTextInput/CustomTextInput";
import Loading from "../../Components/Loading/Loading";
import {
  Mutation,
  MutationResetPasswordArgs,
  Query,
  QueryGeneratorOtpArgs,
} from "../../graphql/__generated__/types";
import { RESET_PASSWORD } from "../../graphql/resetPassword/mutation";
import { GENERATOR_OTP } from "../../graphql/resetPassword/query";

interface InputFormValues {
  otp: string;
  password: string;
  confirmPassword: string;
}

const defaultValues: InputFormValues = {
  otp: "",
  confirmPassword: "",
  password: "",
};

interface ResetContext {
  idUser: number;
  email: string;
}

// L'identifiant et l'adresse arrivent par l'etat du routeur, qui disparait des
// que l'onglet est recharge ou restaure — exactement ce qui se produit quand
// l'utilisateur part lire son code dans sa boite mail et revient. On recopie
// donc le contexte en sessionStorage : il est limite a l'onglet, expire avec
// lui, et n'apparait ni dans l'URL ni dans l'historique de navigation.
const RESET_CONTEXT_KEY = "resetPasswordContext";

const readContext = (state: unknown): ResetContext | null => {
  const fromState = state as Partial<ResetContext> | null;

  if (fromState?.idUser) {
    const context: ResetContext = {
      idUser: Number(fromState.idUser),
      email: fromState.email || "",
    };
    try {
      sessionStorage.setItem(RESET_CONTEXT_KEY, JSON.stringify(context));
    } catch {
      // Navigation privee ou stockage refuse : le parcours reste utilisable
      // tant que l'utilisateur ne recharge pas la page.
    }
    return context;
  }

  try {
    const stored = sessionStorage.getItem(RESET_CONTEXT_KEY);
    if (!stored) {
      return null;
    }
    const context = JSON.parse(stored) as ResetContext;
    return context?.idUser ? context : null;
  } catch {
    return null;
  }
};

const forgetContext = () => {
  try {
    sessionStorage.removeItem(RESET_CONTEXT_KEY);
  } catch {
    // Rien a nettoyer si le stockage est indisponible.
  }
};

const ResetPassword: React.FC = () => {
  // La destructuration directe de "state" plantait l'ecran des que cet etat
  // etait absent : c'est l'erreur "Cannot read properties of null (reading
  // 'idUser')" remontee par les clients. On lit le contexte defensivement,
  // etat du routeur d'abord, sessionStorage ensuite.
  const { state } = useLocation();
  const navigate = useNavigate();

  const [context] = React.useState<ResetContext | null>(() =>
    readContext(state),
  );
  const hasContext = context !== null;
  const email = context?.email || "";

  const [success, setSuccess] = React.useState<boolean>(false);
  const [notice, setNotice] = React.useState<string>("");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<InputFormValues>();
  const [DoResetPassword, { loading }] = useMutation<
    Mutation,
    MutationResetPasswordArgs
  >(RESET_PASSWORD, {
    onCompleted: (data) => {
      if (data.resetPassword?.id) {
        reset(defaultValues);
        forgetContext();
        setNotice("Votre mot de passe a été réinitialisé.");
        setSuccess(true);
      }
    },
  });

  // Le formulaire etait seulement vide apres une reinitialisation reussie :
  // l'utilisateur restait sur l'ecran sans savoir si son mot de passe avait
  // change. On laisse la confirmation visible un court instant, puis on le
  // ramene sur l'ecran de connexion, seule suite possible du parcours.
  React.useEffect(() => {
    if (!success) {
      return undefined;
    }
    const timer = setTimeout(() => navigate("/"), 2000);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  // Le code expire au bout de 5 minutes. Sans ce renvoi, l'utilisateur devait
  // repasser par l'ecran "mot de passe oublie" et ressaisir son adresse pour en
  // obtenir un nouveau. "network-only" est indispensable : la query est en
  // "cache-first" par defaut, et un second appel avec la meme adresse serait
  // servi par le cache, sans qu'aucun code ne soit reellement renvoye.
  const [ResendOtp, { loading: resending }] = useLazyQuery<
    Query,
    QueryGeneratorOtpArgs
  >(GENERATOR_OTP, {
    fetchPolicy: "network-only",
  });

  const handleResendCode = async () => {
    const { data } = await ResendOtp({ variables: { input: { email } } });
    if (data?.GeneratorOtp?.id) {
      // Le code precedent n'est plus valable : on vide le champ pour eviter que
      // l'utilisateur ne revalide celui qu'il avait deja saisi.
      setValue("otp", "");
      setNotice("Un nouveau code vous a été envoyé par e-mail.");
    }
  };

  // Sans contexte — ecran ouvert dans un nouvel onglet, favori sur l'ancienne
  // URL, parcours repris des jours plus tard — il n'y a rien a reinitialiser :
  // on renvoie l'utilisateur demander un code plutot que de le laisser sur un
  // ecran inutilisable.
  React.useEffect(() => {
    if (!hasContext) {
      navigate("/forgotpass", { replace: true });
    }
  }, [hasContext, navigate]);

  const handleResetPassword = (data: InputFormValues) => {
    if (!context) {
      return;
    }
    const { otp, password } = data;
    DoResetPassword({
      variables: {
        input: {
          id: context.idUser,
          otp,
          password,
        },
      },
    });
  };

  if (!hasContext) {
    return null;
  }

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Loading loading={loading} />
      <CustomSnackBar
        open={!!notice}
        setOpen={() => setNotice("")}
        severity="success"
        message={notice}
      />
      <Icon title="unlock" color="error" />
      <Typography>Réinitialiser votre mot de passe ?</Typography>
      <Typography>Veuillez saisir votre nouveau mot de passe</Typography>
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <CustomTextInput
            id="otp *"
            label="Code de reinitialisation *"
            fullWidth={false}
            style={{ width: "50%" }}
            onChange={onChange}
            value={value}
            helperText={errors.otp?.message}
            error={!!errors.otp}
          />
        )}
        name="otp"
        rules={{
          maxLength: {
            value: 6,
            message: "Ce champ doit contenir 6 caracteres",
          },
          minLength: {
            value: 6,
            message: "Ce champ doit contenir 6 caracteres",
          },
          required: "Ce champ est requis .",
        }}
        defaultValue=""
      />
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <CustomTextInput
            id="newpass *"
            label="Nouveau mot de passe"
            type="password"
            fullWidth={false}
            style={{ width: "50%" }}
            onChange={onChange}
            value={value}
            helperText={errors.password?.message}
            error={!!errors.password}
          />
        )}
        name="password"
        rules={{
          required: "Le mot de passe est requis",
        }}
        defaultValue=""
      />

      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <CustomTextInput
            id="otp *"
            label="Confirmation de mot de passe"
            fullWidth={false}
            style={{ width: "50%" }}
            type="password"
            onChange={onChange}
            value={value}
            helperText={errors.confirmPassword?.message}
            error={!!errors.confirmPassword}
          />
        )}
        name="confirmPassword"
        rules={{
          validate: (value) =>
            value === getValues("password") ||
            "Les mots de passe ne correspondent pas",
        }}
        defaultValue=""
      />

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "10px",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit(handleResetPassword)}
        >
          Reinitialiser
        </Button>
        {email ? (
          <Button
            variant="text"
            color="primary"
            onClick={handleResendCode}
            disabled={resending}
            style={{ textTransform: "none", marginLeft: "10px" }}
          >
            {resending ? "Envoi en cours..." : "Renvoyer le code"}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default ResetPassword;
